import * as React from 'react';
import { Column } from 'react-table';
import ReactTable from 'react-table';
import { LocalForm } from 'react-redux-form';
import { ProfileViewModel } from '@models/Auth';
import { dateParser } from '@common/dateParser';
import {
  DocumentHistoryViewModel,
  DocumentHistoryContentModel,
  DocumentMedium,
  DocumentStatus,
  DocumentType
} from '@models/DocumentHistory';
import { TooltipComponent } from '@common/components/tooltip/tooltip.component';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { Link } from 'react-router-dom';
import { DocumentResend } from 'src/document-history/_components/document-resend/document-resend.container';
import { ModalProps } from '@common/components';
import { OwnershipModalComponent } from 'src/service-work-order/dispatch/_components/dispatch-landing/ownership-modal.component';

interface FormModel {
  readonly query: string;
  readonly filterByStatus: string;
  readonly filterByTrade: string;
}

export interface FailedEmailComponentProperties {
  readonly loading?: boolean;
  readonly pageCount: number;
  readonly pageSize: number;
  readonly message: string;
  readonly query?: string;
  readonly pageNumber: number;
  readonly emailList: Array<DocumentHistoryViewModel>;
  readonly user: ProfileViewModel | null;
  readonly toggleDialog: (content: string) => void;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly handleOwnershipChange: (id: number, release: boolean) => void;
  readonly handleRemove: (id: number) => void;

  readonly getEmailList: (
    pageSize: number,
    pageNumber: number,
    orderBy: string,
    orderDirection: string,
    query?: string
  ) => void;
}

interface FailedEmailComponentState {
  priority?: string;
  trade?: string;
}

export class FailedEmailComponent extends React.Component<FailedEmailComponentProperties, FailedEmailComponentState> {
  constructor(props: FailedEmailComponentProperties) {
    super(props);
    this.state = {};
  }

  parseTime(d1: Date) {
    let parsedd1 = dateParser.parse(d1);
    let now = dateParser.now();
    let seconds = dateParser.diff(now, parsedd1, 'seconds');
    let days = Math.floor(seconds / (3600 * 24));
    seconds -= days * 3600 * 24;
    let hrs = Math.floor(seconds / 3600);

    return (
      <div>
        {' '}
        {days} Days/
        {hrs} Hours{' '}
      </div>
    );
  }

  renderDialog(content: DocumentHistoryContentModel[]) {
    this.props.toggleDialog(content[0].text);
  }

  renderEmail(content: DocumentHistoryContentModel[]) {
    return content.map((doc: DocumentHistoryContentModel, index: number) => (
      <a href={`/api/document/${doc.documentId}/download`} key={doc.documentName}>
        <TooltipComponent side="left" icon={index === 0 ? 'fa-file' : 'fa-paperclip'} relative={true}>
          {doc.documentName}
        </TooltipComponent>
      </a>
    ));
  }

  showResendModal = (emailHistoryId: number, entityId?: number, email?: string) => {
    this.props.toggleModal({
      title: 'Resend Document',
      component: DocumentResend,
      hideSave: true,
      hideX: true,
      closeLabel: 'Cancel',
      isSmall: true,
      childProps: {
        emailHistoryId: emailHistoryId,
        entityId: entityId,
        emailAddress: email,
        isFailedEmail: true
      }
    });
  };

  renderActions = (id?: number, entityId?: number, email?: string) => {
    if (id) {
      return (
        <i
          className="fas fa-envelope red-text pointer"
          onClick={() => {
            this.showResendModal(id, entityId, email);
          }}
        />
      );
    } else {
      return;
    }
  };

  handleOwnershipClick = (row: DocumentHistoryViewModel): void => {
    if (this.props.user && !this.props.loading) {
      if (this.props.user.employeeId && row.ownerEmployeeId && this.props.user.employeeId !== row.ownerEmployeeId) {
        this.props.toggleModal({
          title: 'Ownership Confirmation',
          isSmall: true,
          component: OwnershipModalComponent,
          saveLabel: 'Yes',
          closeLabel: 'No',
          saveCallback: () => {
            this.props.handleOwnershipChange(row.emailHistoryId || 0, false);
            this.props.toggleModal();
          },
          closeCallback: this.props.toggleModal,
          childProps: { entityType: 'Failed Email' }
        });
      } else {
        const release: boolean =
          this.props.user &&
          this.props.user.employeeId &&
          row.ownerEmployeeId &&
          this.props.user.employeeId === +row.ownerEmployeeId
            ? true
            : false;

        this.props.handleOwnershipChange(row.emailHistoryId || 0, release);
      }
    }
  };

  render() {
    const columns: Column[] = [
      {
        accessor: (d: DocumentHistoryViewModel) =>
          d ? (
            <a className={this.props.loading ? 'disabled' : 'active'} onClick={() => this.handleOwnershipClick(d)}>
              <i className="fas fa-fw fa-hand-paper pointer" />
            </a>
          ) : (
            'N/A'
          ),
        id: 'ownership',
        Header: 'Ownership',
        sortable: false,
        maxWidth: 75
      },
      {
        accessor: (row: DocumentHistoryViewModel) => (
          <div>{row.owner ? row.owner.firstName.concat(' ', row.owner.lastName || '') : ''}</div>
        ),
        Header: 'Owner',
        id: 'owner',
        sortable: false
      },
      {
        accessor: (d: DocumentHistoryViewModel) => {
          return d.date;
        },
        id: 'date',
        Header: 'Sent Date',
        sortable: true,
        defaultSortDesc: true,

        Cell: (props: { value: string }) => <>{dateParser.parse(props.value).format('l LT')}</>
      },
      {
        accessor: (d: DocumentHistoryViewModel) => {
          return d.recipient;
        },
        id: 'recipient',
        Header: 'Recipient',
        sortable: false
      },
      {
        accessor: (d: DocumentHistoryViewModel) => (d.entityId !== 0 ? <>{this.renderEntityLink(d)}</> : 'N/A'),
        id: 'entity',
        Header: 'Link',
        sortable: false
      },
      {
        accessor: (d: DocumentHistoryViewModel) => {
          return DocumentStatus[d.status];
        },
        id: 'status',
        Header: 'Delivery Status',
        sortable: true,
        defaultSortDesc: true
      },
      {
        accessor: (d: DocumentHistoryViewModel) => {
          return DocumentType[d.type];
        },
        id: 'type',
        Header: 'Type',
        sortable: false
      },
      {
        style: { overflow: 'visible' },
        accessor: (d: DocumentHistoryViewModel) => {
          return d.medium === DocumentMedium.Email ? (
            this.renderEmail(d.content)
          ) : (
            <a
              href="javascript:void(0);"
              className="ellipsis"
              onClick={() => {
                this.renderDialog(d.content);
              }}
            >
              View
            </a>
          );
        },
        id: 'content',
        Header: 'Content',
        maxWidth: 75,
        sortable: false
      },
      {
        Header: 'Action',
        id: 'action',
        maxWidth: 50,
        sortable: false,
        accessor: (d: DocumentHistoryViewModel) => {
          return this.renderActions(d.emailHistoryId, d.entityId, d.recipient);
        }
      },
      {
        Header: 'Remove',
        id: 'remove',
        maxWidth: 75,
        sortable: false,
        accessor: (d: DocumentHistoryViewModel) => {
          return (
            <i
              className="fas fa-times red-text pointer"
              onClick={() => {
                this.props.handleRemove(d.emailHistoryId || 0);
              }}
            />
          );
        }
      }
    ];
    return (
      <div className="row">
        <div className="col-md-12">
          <LocalForm
            className="form-horizontal"
            hideNativeErrors={true}
            onSubmit={(props: FormModel) => this.handleSearch(props.query)}
          >
            <div className="row between-xs top-xs">
              <h3>Failed Emails</h3>
            </div>
          </LocalForm>
          <ReactTable
            onPageChange={() => {
              Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                element.scrollTop = 0;
              });
            }}
            className="-striped -highlight"
            columns={columns}
            data={this.props.emailList}
            defaultPageSize={10}
            loading={this.props.loading}
            manual={true}
            onFetchData={this.handleFetchData}
            pages={this.props.pageCount}
          />
        </div>
      </div>
    );
  }

  private renderEntityLink(row: DocumentHistoryViewModel) {
    var location: string = '';
    switch (row.type) {
      case DocumentType.ContractConfirmation:
        location = RouteDefinitions.contract.Details;
        break;
      case DocumentType.ContractRenewal:
        location = RouteDefinitions.contract.Details;
        break;
      case DocumentType.ContractResale:
        location = RouteDefinitions.contract.Details;
        break;
      default:
        location = RouteDefinitions.dashboard;
        break;
    }
    return <Link to={getRoute(location, { id: +row.entityId! })}>View Details</Link>;
  }

  private handleFetchData = (state: {
    page: number;
    pageSize: number;
    showInactive: boolean;
    sorted: Array<{ id: number; desc: string }>;
  }): void => {
    const orderBy = state.sorted[0] ? state.sorted[0].id.toString() : 'date';
    const orderDirection = state.sorted[0] ? (state.sorted[0].desc ? 'desc' : 'asc') : 'desc';

    this.props.getEmailList(state.pageSize, state.page + 1, orderBy, orderDirection, this.props.query);
  };

  private handleSearch = (query: string): void => {
    this.props.getEmailList(this.props.pageSize, 1, 'date', 'desc', query);
  };
}



// WEBPACK FOOTER //
// ./src/failed-email/_components/failed-email.component.tsx